import { ThemeOptions as ToolkitThemeOptions } from '@ethings/ui-toolkit';
import { colors, createTheme, Theme, ThemeOptions } from '@mui/material';
import { deepmerge } from '@mui/utils';
import chroma from 'chroma-js';
import { createMakeStyles } from 'tss-react';

type ThemeOptionsWithInternals = ThemeOptions & {
  palette?: ThemeOptions['palette'] | null;
  vars?: unknown;
};

const BASE_THEME_OPTIONS: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#0097FE',
    },
    secondary: {
      main: '#512DA8',
    },
  },
  shape: {
    borderRadius: 8,
  },
  landingPage: {
    footer: {
      logo: 'dark',
    },
  },
  scrollbar: {
    width: '10px',
    height: '10px',
    borderRadius: '8px',
  },
  logo: {
    light: '#ffffff',
    dark: '#231F20',
  },
  marker: {
    icon: {
      fill: '#FFFFFF',
      size: '1rem',
    },
    dot: {
      size: '26px',
    },
  },
  mapPopup: {
    padding: 1,
    width: 320,
    poiWidth: 300,
  },
  sidebar: {
    icons: {
      size: '1.5rem',
    },
  },
  dataCard: {
    avatarSize: '3rem',
  },
  history: {
    colors: ['#1E75D8', '#00A389', '#F59E0B', '#8B5CF6', '#EF4444'],
  },
  typography: {
    fontFamily: '"Google Sans Flex", sans-serif',
    body1: {
      fontWeight: 400,
    },
    body2: {
      fontWeight: 400,
    },
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    dataCardLabel: {
      fontSize: '0.75rem',
      fontWeight: 400,
    },
    dataCardTitle: {
      fontSize: '1rem',
      fontWeight: 400,
    },
    dataCardHint: {
      fontSize: '0.75rem',
      fontWeight: 400,
    },
  },
  states: {
    off: '#1a1a1a',
    on: '#61b902',
    paused: '#fac710',
    uncertain: '#cccccc',
    unknown: '#808080',
  },
  tooltip: {
    background: 'rgba(97, 97, 97, 0.92)',
    contrastText: '#ffffff',
  },
  zIndex: {
    appBar: 1250,
  },
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const sanitizeThemeOptions = (options: unknown): ThemeOptions => {
  if (!isRecord(options)) {
    return {};
  }

  const sanitized = { ...options } as ThemeOptionsWithInternals;

  delete sanitized.vars;

  if ('palette' in sanitized && !isRecord(sanitized.palette)) {
    delete sanitized.palette;
  }

  return sanitized;
};

export const createThemeOptions = (custom: ThemeOptions = {}): ToolkitThemeOptions => {
  return deepmerge(BASE_THEME_OPTIONS, sanitizeThemeOptions(custom)) as ToolkitThemeOptions;
};

export const theme = (custom: ThemeOptions = {}): Theme => {
  const intermediate = createTheme(deepmerge(BASE_THEME_OPTIONS, sanitizeThemeOptions(custom)));
  const intermediateHistory = (intermediate as Theme & { history?: { colors?: string[] } }).history;
  const baseHistoryColors = (BASE_THEME_OPTIONS as ThemeOptions & { history?: { colors?: string[] } }).history?.colors ?? [];
  const sidebarBackgroundColor = intermediate.sidebar?.background ?? intermediate.palette.grey[100];
  const sidebarSecondaryBackgroundColor =
    intermediate.sidebar?.secondaryBackground ??
    (intermediate.sidebar?.background
      ? chroma(intermediate.sidebar.background).darken(0.2).css()
      : intermediate.palette.grey[200]);
  const sidebarDividerColor = intermediate.sidebar.divider ?? intermediate.palette.divider;
  const sidebarActiveMenuItemBackground = intermediate.sidebar.activeMenuItem?.background ?? intermediate.palette.primary.main;
  const sidebarActiveMenuItemText = intermediate.sidebar.activeMenuItem?.text ?? intermediate.palette.primary.contrastText;
  const sidebarTextColor = intermediate.sidebar?.contrastText ?? intermediate.palette.text.primary;
  const sidebarCloseBackground = intermediate.sidebar?.close?.background ?? sidebarBackgroundColor;
  const sidebarCloseHoverBackground =
    intermediate.sidebar?.close?.hoverBackground ??
    (intermediate.sidebar?.close?.background
      ? chroma(intermediate.sidebar.close.background).darken(0.2).css()
      : sidebarSecondaryBackgroundColor);

  return createTheme(intermediate, {
    palette: {
      background: {
        default: chroma.mix(
          intermediate.palette.background.default,
          chroma(intermediate.palette.text.primary).alpha(1),
          0.05,
        ).css(),
      },
    },
    components: {
      MuiCardActions: {
        styleOverrides: {
          root: {
            paddingLeft: intermediate.spacing(3),
            paddingRight: intermediate.spacing(3),
            paddingTop: intermediate.spacing(3),
            paddingBottom: 0,
            '&:last-child': {
              paddingBottom: intermediate.spacing(3),
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            height: 'min-content',
            overflow: 'unset',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            paddingLeft: intermediate.spacing(3),
            paddingRight: intermediate.spacing(3),
            paddingTop: intermediate.spacing(3),
            paddingBottom: 0,
            '&:last-child': {
              paddingBottom: intermediate.spacing(3),
            },
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            paddingLeft: intermediate.spacing(3),
            paddingRight: intermediate.spacing(3),
            paddingTop: intermediate.spacing(3),
            paddingBottom: 0,
            '&:last-child': {
              paddingBottom: intermediate.spacing(3),
            },
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            '& svg': {
              fontSize: '1.5rem',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingLeft: intermediate.spacing(3),
            paddingRight: intermediate.spacing(3),
          },
        },
      },
      MuiCalendarPicker: {
        styleOverrides: {
          root: {
            maxHeight: '300px',
            '& .PrivatePickersSlideTransition-root': {
              minHeight: '200px',
            },
            '& .MuiMonthPicker-root': {
              width: '300px',
            },
            '& .PrivatePickersMonth-root': {
              border: `solid 4px ${intermediate.palette.background.paper}`,
              borderRadius: intermediate.shape.borderRadius,
              height: '60px',
            },
            '& .Mui-selected': {
              fontSize: intermediate.typography.subtitle1.fontSize,
            },
          },
        },
      },
      MuiStack: {
        styleOverrides: {
          root: {
            '& .ListPaperColumn-root': {
              overflow: 'hidden',
            },
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          getRowHeight: () => 'auto',
        },
        styleOverrides: {
          root: {
            border: 'none',
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
              padding: `0 ${intermediate.spacing(2)}`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
              paddingTop: intermediate.spacing(1),
              padding: intermediate.spacing(1),
            },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
              paddingTop: intermediate.spacing(2),
              padding: intermediate.spacing(2),
            },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
              paddingTop: intermediate.spacing(3),
              padding: intermediate.spacing(3),
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
            '&.MuiDataGrid-root.grid-pointer-rows .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          enterTouchDelay: 0,
        },
      },
    },
    alerts: {
      amber: '#ffc107',
      blue: '#2196f3',
      default: colors.grey[600],
      green: '#357a38',
      orange: '#fd906e',
      purple: '#9c27b0',
      red: '#f44336',
      teal: '#1de9b6',
      white: '#ffffff',
    },
    colorMap: [
      '#2196F3',
      '#FF4528',
      '#51D751',
      '#FFD633',
      '#B21CB5',
      '#A96E37',
      '#FF69B4',
      '#FF7F4D',
      '#8F00FF',
      '#F04B74',
    ],
    drawer: {
      puller: '#757575',
    },
    eventBreadcrumb: {
      duress: {
        fill: '#fac710',
        stroke: '#fac710',
      },
      first: {
        fill: '#61b902',
        stroke: '#61b902',
      },
      hoverCursor: {
        fill: '#ffffff',
        stroke: '#0000ff',
      },
      intermediary: {
        fill: '#808080',
        stroke: '#808080',
      },
      last: {
        fill: '#2196f3',
        stroke: intermediate.palette.common.white,
      },
      rollOver: {
        fill: '#f44336',
        stroke: '#f44336',
      },
    },
    incident: {
      clusterBackgroundColor: '#f5c449',
      clusterBorder: `${chroma('#f5c449').alpha(.50).css()} solid .25rem`,
      roadColor: '#f44336',
      size: '1.5rem',
    },
    landing: {
      signInButton: '#f24726',
      signInButtonDark: '#d13819',
    },
    lastContact: {
      current: intermediate.palette.success.main,
      recent: intermediate.palette.warning.main,
      offline: intermediate.palette.grey[500],
    },
    map: {
      cluster: {
        spiderLeg: '#222',
      },
      drawing: {
        color: '#0D99FF',
      },
      editControl: {
        disabled: '#f4f4f4',
      },
    },
    pin: {
      search: '#f24726',
    },
    placeholder: {
      image: '#9e9e9e',
    },
    puller: '#757575',
    videoSegment: {
      scrollbarHeight: '16px',
      tableBorder: `1px solid ${chroma(intermediate.palette.common.black).alpha(0.12).css()}`,
      videoCellBoxShadow: `0px 2px 1px -1px ${chroma(intermediate.palette.common.black).alpha(0.2).css()}, 0px 1px 1px ${chroma(intermediate.palette.common.black).alpha(0.14).css()}, 0px 1px 3px ${chroma(intermediate.palette.common.black).alpha(0.12).css()}`,
      videoCellFocusedBoxShadow: `0px 7px 9px -4px ${chroma(intermediate.palette.common.black).alpha(0.2).css()},0px 14px 21px 2px ${chroma(intermediate.palette.common.black).alpha(0.14).css()},0px 5px 26px 4px ${chroma(intermediate.palette.common.black).alpha(0.12).css()}`,
    },
    videoStatus: {
      notAvailable: '#757575',
      noLongerAvailable: '#808080',
      recorded: '#0299D6',
      requested: '#F2994A',
      uploadComplete: '#1F9F57',
      uploadCompleteVisited: '#75C196',
      uploadFailed: '#EB5757',
      uploadStarted: '#F2C94C',
    },
    marker: {
      background: intermediate.marker.background || intermediate.palette.primary.main,
    },
    mapPopup: {
      background: intermediate.mapPopup?.background || intermediate.palette.grey[100],
      borderRadius: intermediate.mapPopup?.borderRadius || intermediate.shape.borderRadius,
      contrastText: intermediate.mapPopup?.contrastText || intermediate.palette.text.primary,
    },
    heading: {
      icon: {
        color: intermediate.heading?.icon?.color || intermediate.palette.primary.main,
      },
    },
    landingPage: {
      background: intermediate.landingPage.background || intermediate.palette.grey[100],
      footer: {
        contrastText: intermediate.landingPage.footer?.contrastText || intermediate.palette.text.primary,
      },
      particleColor: intermediate.landingPage.particleColor || intermediate.palette.grey[400],
      signInButton: {
        background: intermediate.landingPage.signInButton?.background || intermediate.palette.primary.main,
        contrastText: intermediate.landingPage.signInButton?.contrastText || intermediate.palette.primary.contrastText,
      },
    },
    sidebar: {
      activeMenuItem: {
        background: sidebarActiveMenuItemBackground,
        text: sidebarActiveMenuItemText,
      },
      contrastText: sidebarTextColor,
      background: sidebarBackgroundColor,
      secondaryBackground: sidebarSecondaryBackgroundColor,
      divider: sidebarDividerColor,
      icons: {
        color: intermediate.sidebar.icons?.color || sidebarTextColor,
      },
      avatar: {
        background: intermediate.sidebar.avatar?.background || sidebarBackgroundColor,
        iconColor: intermediate.sidebar.avatar?.iconColor || sidebarTextColor,
      },
      close: {
        background: sidebarCloseBackground,
        contrastText: intermediate.sidebar.close?.contrastText || sidebarTextColor,
        hoverBackground: sidebarCloseHoverBackground,
      },
    },
    wizard: {
      header: {
        background: intermediate.wizard?.header?.background || intermediate.palette.grey[100],
        contrastText: intermediate.wizard?.header?.contrastText || intermediate.palette.text.primary,
        progressColor: intermediate.wizard?.header?.progressColor || intermediate.palette.primary.main,
        subText: intermediate.wizard?.header?.subText || intermediate.palette.text.secondary,
      },
    },
    dataCard: {
      avatarSize: intermediate.dataCard.avatarSize,
      background: intermediate.dataCard?.background || intermediate.palette.grey[200],
      contrastText: intermediate.dataCard?.contrastText || intermediate.palette.text.primary,
      borderRadius: intermediate.dataCard?.borderRadius || intermediate.shape.borderRadius,
    },
    history: {
      colors: intermediateHistory?.colors ?? baseHistoryColors,
    },
    scrollbar: {
      width: intermediate.scrollbar.width,
      height: intermediate.scrollbar.height,
      borderRadius: intermediate.scrollbar.borderRadius,
    },
  });
};

const createBaseTheme = (custom?: ThemeOptions): Theme => {
  return (theme(custom));
};

export const {
  makeStyles,
  useStyles,
} = createMakeStyles({ useTheme: createBaseTheme });
